import React, { useState } from 'react';
import { X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) return toast.error('Fill in both passwords');
    if (next.length < 6) return toast.error('New password must be at least 6 characters');
    if (next !== confirm) return toast.error('New passwords do not match');
    if (next === current) return toast.error('New password must be different from current');

    setSaving(true);
    try {
      await api.changePassword(current, next);
      toast.success('Password updated');
      reset();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="surface rounded-lg w-full max-w-md overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <Lock size={14} className="text-amber-400" /> Change Password
          </h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Current Password
            </label>
            <input
              type="password"
              autoFocus
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              New Password
            </label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              placeholder="At least 6 characters"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-900 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
          >
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-amber text-xs px-4 py-1.5 rounded-md disabled:opacity-60">
            {saving ? 'Saving…' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  );
}
